// MACHINERY V2 (Stroje) — ChromeNavV4, the 52px platform nav bar (M2.1).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §4.1 #1 + §9 D2.
// Reference impl: design_handoff_machinery_v3/machinery-chrome-nav.jsx
// (ChromeNavV4 / NavTab / MidasBtn).
//
// D2 IS RESOLVED: this is the FUTURE PERMANENT PLATFORM NAV. It renders only
// on v2 pages during the build and is promoted app-wide at M2.12, when the
// legacy Header top bar retires with NO reconciliation back. It is therefore
// built to permanent quality — real permission gating, real navigation, real
// menus — not as a v2 mock.
//
// Composition (artboard, left to right):
//   logo · 4 ALWAYS-LABELLED module tabs · spacer · 28px Midas button ·
//   divider · SK ▾ language menu · bell · name + avatar (user menu)
//
// Rules encoded here:
//   • Coming-soon modules are HIDDEN, never greyed — the legacy bar's two
//     grey mystery icons (Sledovanie, Skladové centrum) simply do not exist
//     in this nav.
//   • A tab the user cannot enter is hidden too (permission + company-type
//     gate, mirroring the legacy bar, including its transition-mode rule:
//     a token with NO permissions claim sees everything).
//   • Stroje has NO sub-row. The artboard's 40px sub-row + tab deep-jump
//     dropdown (for Service Hub / Sieť partnerov / Operácie) are deliberately
//     NOT built at M2.1: while the build runs, those modules still render the
//     legacy bar with its own sub-nav, so nothing is unreachable. They land
//     with the M2.12 promotion — see machinery-chrome-nav.jsx NavTab(subs).
//   • The module accent tints the ACTIVE tab and the avatar only; inactive
//     tabs stay var(--muted) (artboard NavTab: color = active ? acc : muted).
//     That is why the sibling .mod-* accent blocks stay dropped from
//     machinery-v2-tokens.scss — the accents live in navModules.ts, exactly
//     as the handoff's NAV_ACCENTS map carries them.
//   • The Midas button uses the BRAND accent (--accent), never a module
//     accent — it is global, reachable from every module.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import { useNavigate } from "react-router-dom";

import { Modules } from "../../../../core/models/PermissionModels";
import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { NgBell } from "./ChromeNavIcons";
import { ChromeLangMenu, ChromeUserMenu } from "./ChromeRightMenus";
import { ChromeNavModuleKey, NAV_MODULES, canSeeModule, useTypeAllows } from "./navModules";

export interface ChromeNavV4Props {
  /** Which module owns the current page. v2 only ever renders "stroje";
   *  the prop exists for the M2.12 app-wide promotion. */
  active?: ChromeNavModuleKey;
}

const ChromeNavV4: React.FC<ChromeNavV4Props> = ({ active = "stroje" }) => {
  const { translate } = useTranslation(["layout", "common"]);
  const navigate = useNavigate();
  const isBotEnabled = useAppSelector((s) => s.chatBot.isBotEnabled);
  // READ-ONLY on purpose: the legacy bell still owns fetchUnreadCount, the
  // SignalR hub subscription AND the notification panel. M2.12's right-cluster
  // audit MUST re-home all three here before the legacy bar retires — if it
  // does not, this badge silently reads 0 forever: no error, no empty state,
  // just a bell that never lights up.
  const unreadCount = useAppSelector((s: any) => s.notifications?.unreadCount ?? 0);

  // Company-type gate. ONE implementation, in navModules.ts beside
  // `canSeeModule`, because the phone drawer asks the same question — including
  // the transition escape hatch, which is the clause a fork loses first.
  const typeAllows = useTypeAllows();

  const activeAccent =
    NAV_MODULES.find((m) => m.key === active)?.accent || "var(--mod-accent)";

  const canMidas = isBotEnabled && canSeeModule(Modules.Midas);
  // /notifications sits behind PermissionRouteGuard(Notifications): without
  // this gate a Notifications:None user gets a bell that silently redirects.
  const canNotifications = canSeeModule(Modules.Notifications);

  return (
    <div
      style={{
        height: 52,
        flexShrink: 0,
        display: "flex",
        alignItems: "stretch",
        gap: 2,
        padding: "0 20px",
        background: "var(--card)",
        borderBottom: "1px solid var(--border)",
        position: "relative",
        zIndex: 5,
      }}
    >
      {/* Wordmark. Non-interactive on purpose while v2 is a parallel surface;
          M2.12 may wire it to the default route like the legacy bar does. */}
      <span style={{ display: "inline-flex", alignItems: "center", marginRight: 14 }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: "var(--ink)",
            whiteSpace: "nowrap",
          }}
        >
          MACHINE
          <span
            style={{
              background: "var(--ink)",
              color: "var(--bg)",
              borderRadius: 3,
              padding: "1px 4px",
              marginLeft: 2,
            }}
          >
            ID
          </span>
        </span>
      </span>

      {NAV_MODULES.filter(
        (m) => canSeeModule(m.module) && typeAllows(m.operatorOnly)
      ).map((m) => {
        const isActive = m.key === active;
        const label = translate(m.labelKey);
        return (
          <button
            key={m.key}
            type="button"
            className={isActive ? "mv2-tab is-active" : "mv2-tab"}
            aria-current={isActive ? "page" : undefined}
            onClick={() => navigate(m.path)}
            title={label}
            style={{
              // L6 (USER-LANG-IT-PL-1): the bar needs ~900px in EN and ~980/970/1030px in
              // DE/IT/PL, so at 768-1023 the right cluster (bell, user menu) was pushed off-screen.
              // The ACTIVE tab keeps its full label; inactive labels may ellipsize (title = full).
              flexShrink: isActive ? 0 : 1,
              minWidth: 0,
              alignSelf: "stretch",
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "0 13px",
              border: 0,
              background: "transparent",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: isActive ? 600 : 450,
              color: isActive ? m.accent : "var(--muted)",
              whiteSpace: "nowrap",
            }}
          >
            <m.Icon />
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
            {isActive && (
              <span
                style={{
                  position: "absolute",
                  left: 10,
                  right: 10,
                  bottom: 0,
                  height: 2,
                  background: m.accent,
                }}
              />
            )}
          </button>
        );
      })}

      <span style={{ flex: 1 }} />

      <span style={{ display: "inline-flex", alignItems: "center", gap: 14 }}>
        {canMidas && (
          <button
            type="button"
            title={translate("layout:chromeV4.midas")}
            aria-label={translate("layout:chromeV4.midas")}
            onClick={() => navigate("/midas")}
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              border: 0,
              flexShrink: 0,
              background: "var(--accent)",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(15,23,30,0.18)",
            }}
          >
            M
          </button>
        )}
        <span style={{ width: 1, height: 20, background: "var(--border)", alignSelf: "center" }} />
        <ChromeLangMenu />
        {canNotifications && (
          <button
            type="button"
            className="mv2-bell"
            title={translate("layout:chromeV4.notifications")}
            aria-label={translate("layout:chromeV4.notifications")}
            onClick={() => navigate("/notifications")}
            style={{
              position: "relative",
              border: 0,
              background: "transparent",
              padding: 0,
              color: "var(--muted)",
              display: "inline-flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <NgBell />
            {unreadCount > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: -3,
                  right: -5,
                  minWidth: 14,
                  height: 14,
                  padding: "0 3px",
                  borderRadius: 999,
                  background: "var(--red)",
                  color: "#fff",
                  fontFamily: "var(--font-mono)",
                  fontSize: 9,
                  fontWeight: 700,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        )}
        <ChromeUserMenu accent={activeAccent} />
      </span>
    </div>
  );
};

export default ChromeNavV4;
